import React, { useState, useEffect } from 'react';
import './DataHealthChatMessage.css';

interface DataHealthOption {
  value: string;
  label: string;
  is_default: boolean;
}

interface ActionQuestion {
  issue_type: string;
  column: string;
  question: string;
  options: DataHealthOption[];
  ai_suggestion: string;
  reasoning: string;
}

interface DataHealthChatMessageProps {
  summary: string;
  actionQuestions: ActionQuestion[];
  hasSavedPreferences?: boolean;
  onSubmit: (choices: Record<string, string>) => void;
  onSkip: () => void;
}

const DataHealthChatMessage: React.FC<DataHealthChatMessageProps> = ({
  summary,
  actionQuestions,
  hasSavedPreferences = false,
  onSubmit,
  onSkip
}) => {
  const [selectedChoices, setSelectedChoices] = useState<Record<string, string>>({});
  const [expandedQuestions, setExpandedQuestions] = useState<Record<string, boolean>>({});
  const [hasInteracted, setHasInteracted] = useState(false);

  // Initialize with default choices
  useEffect(() => {
    const defaults: Record<string, string> = {};
    actionQuestions.forEach(q => {
      const defaultOption = q.options.find(opt => opt.is_default);
      if (defaultOption) {
        defaults[q.issue_type] = defaultOption.value;
      }
    });
    setSelectedChoices(defaults);
  }, [actionQuestions]);

  const handleOptionChange = (issueType: string, value: string) => {
    setSelectedChoices(prev => ({
      ...prev,
      [issueType]: value
    }));
    setHasInteracted(true);
  };

  const toggleReasoning = (issueType: string) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [issueType]: !prev[issueType]
    }));
  };

  const handleSubmit = () => {
    onSubmit(selectedChoices);
  };

  const handleQuickAccept = () => {
    // Use all default selections
    const defaults: Record<string, string> = {};
    actionQuestions.forEach(q => {
      const defaultOption = q.options.find(opt => opt.is_default);
      if (defaultOption) {
        defaults[q.issue_type] = defaultOption.value;
      }
    });
    onSubmit(defaults);
  };

  const allQuestionsAnswered = actionQuestions.every(
    q => selectedChoices[q.issue_type]
  );

  if (actionQuestions.length === 0) {
    return (
      <div className="data-health-chat-message success-message">
        <div className="message-header">
          <span className="icon">✅</span>
          <span className="title">Data Quality Check</span>
        </div>
        <p className="message-content">{summary || "Data quality check passed - no issues detected!"}</p>
        <div className="message-actions">
          <button className="btn-continue" onClick={() => onSubmit({})}>
            Continue to Script Generation
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="data-health-chat-message">
      <div className="message-header">
        <span className="icon">📊</span>
        <span className="title">Data Quality Check</span>
      </div>
      
      <p className="message-summary">{summary}</p>

      {hasSavedPreferences && !hasInteracted && (
        <div className="quick-actions">
          <p className="quick-actions-text">
            I'll use your saved preferences for these issues. You can customize if needed.
          </p>
          <div className="quick-action-buttons">
            <button className="btn-quick-accept" onClick={handleQuickAccept}>
              Use Saved Preferences
            </button>
            <button className="btn-customize" onClick={() => setHasInteracted(true)}>
              Let Me Review
            </button>
          </div>
        </div>
      )}

      <div className="issues-list">
        {actionQuestions.map((question, index) => (
          <div key={question.issue_type} className="issue-item">
            <div className="issue-header">
              <span className="issue-number">{index + 1}.</span>
              <span className="issue-text">{question.question}</span>
            </div>

            <div className="issue-options">
              {question.options.map(option => (
                <label key={option.value} className="option-label">
                  <input
                    type="radio"
                    name={`issue-${question.issue_type}`}
                    value={option.value}
                    checked={selectedChoices[question.issue_type] === option.value}
                    onChange={() => handleOptionChange(question.issue_type, option.value)}
                  />
                  <span className="option-text">
                    {option.label}
                    {option.is_default && !hasInteracted && (
                      <span className="recommended-badge">Recommended</span>
                    )}
                  </span>
                </label>
              ))}
            </div>

            {question.reasoning && (
              <button
                className="reasoning-toggle"
                onClick={() => toggleReasoning(question.issue_type)}
              >
                {expandedQuestions[question.issue_type] ? '▼' : '▶'} Why this suggestion?
              </button>
            )}

            {expandedQuestions[question.issue_type] && question.reasoning && (
              <div className="reasoning-content">
                {question.reasoning}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="message-actions">
        <button 
          className="btn-primary"
          onClick={handleSubmit}
          disabled={!allQuestionsAnswered}
        >
          Apply & Continue
        </button>
        <button 
          className="btn-secondary"
          onClick={onSkip}
        >
          Skip Data Checks
        </button>
      </div>
    </div>
  );
};

export default DataHealthChatMessage;